/**
 * Core API utilities
 */

import { toaster } from "@/components/ui/toaster";
import { log } from '@/lib/log';
import { reportApiFailure } from '@/lib/telemetry';
import { authContinuation, loginPath } from '@/lib/auth/continuation';
import { buildApiUrl, resolveApiBaseUrl } from '@/lib/apiBaseUrl';
import type { FetchOptions, ApiError, QueryParams } from './types';

// Centralized API configuration
export const API_CONFIG = {
  // Shared with the CSP builder so the policy always names the origin these
  // requests actually go to; see lib/apiBaseUrl.ts.
  BASE_URL: resolveApiBaseUrl(),
  getUrl: (endpoint: string) => buildApiUrl(endpoint, API_CONFIG.BASE_URL),
};

const isTestEnvironment = process.env.NODE_ENV === 'test';

// Cache for storing API responses
const apiCache = new Map<string, {data: unknown, timestamp: number}>();
const CACHE_TTL = 60000; // 60 seconds

function isExpiredJwt(token: string): boolean {
  try {
    const segment = token.split('.')[1];
    if (!segment) return false;
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof payload.exp === 'number' && payload.exp <= Math.floor(Date.now() / 1000);
  } catch {
    // Some test and transitional credentials are opaque. The backend remains
    // authoritative for those; only a JWT with a clear expired claim is denied.
    return false;
  }
}

function authToken(): string | null {
  const token = localStorage.getItem('auth_token');
  if (token && isExpiredJwt(token)) {
    localStorage.removeItem('auth_token');
    return null;
  }
  return token;
}

/** Redact capability-bearing public URLs before logging/reporting. */
export function redactApiEndpoint(endpoint: string): string {
  return endpoint
    .replace(/(\/api\/public\/placement-tests\/)[^/?]+/g, '$1[REDACTED]')
    .replace(/(\/api\/public\/certificates\/)[^/?]+/g, '$1[REDACTED]')
    .replace(/([?&](?:token|continuation_token)=)[^&]*/gi, '$1[REDACTED]');
}

export function clearApiCache() {
  apiCache.clear();
}

function getCachedData(cacheKey: string) {
  const cached = apiCache.get(cacheKey);
  if (!cached) {
    log.debug(`Cache miss for: ${cacheKey}`);
    return null;
  }

  const now = Date.now();
  if (now - cached.timestamp > CACHE_TTL) {
    log.debug(`Cache expired for: ${cacheKey}`);
    apiCache.delete(cacheKey);
    return null;
  }

  log.debug(`Cache hit for: ${cacheKey}`);
  return cached.data;
}

/**
 * Correlation id sent as `X-Request-Id` on every API call. The backend adopts
 * an inbound id, so a client failure report and the server log line share it.
 */
function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function redirectToLogin() {
  if (typeof window !== 'undefined') {
    if (isTestEnvironment) return;
    const destination = authContinuation(
      `${window.location.pathname}${window.location.search}`
    );
    try {
      window.location.href = loginPath(destination);
    } catch {
      // JSDOM throws for document navigation; the real browser path still works.
    }
  }
}

/**
 * Build query string from params object
 */
export function buildQueryString(params?: QueryParams): string {
  if (!params) return '';

  const parts = Object.keys(params)
    .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
    .map(key => {
      const value = params[key];
      if (Array.isArray(value)) {
        return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value as string | number)}`;
    });

  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Add default pagination to params
 */
export function withPagination(params?: QueryParams, skipIfIds = false): QueryParams {
  if (skipIfIds && params?.ids) {
    return { ...params };
  }
  return {
    page: params?.page || 1,
    limit: params?.limit || 10,
    ...params
  };
}

/**
 * Centralized function for making fetch requests to the API
 */
export async function fetchApi<T = unknown>(
  endpoint: string,
  options: FetchOptions = {},
  componentName: string = 'Unknown'
): Promise<T> {
  const {
    requireAuth = true,
    handleUnauthorized,
    ...fetchOptions
  } = options;

  const method = fetchOptions.method || 'GET';
  const diagnosticEndpoint = redactApiEndpoint(endpoint);
  log.debug(`[API Call from ${componentName}] ${method} ${diagnosticEndpoint}`);
  const requestId = newRequestId();
  const useCache = method === 'GET';
  const cacheKey = useCache ? `${endpoint}:${JSON.stringify(fetchOptions)}` : '';

  // Caching is disabled but leaving infrastructure
  if (false) {
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      return cachedData as T;
    }
  }

  const headers = new Headers(fetchOptions.headers || {});
  headers.set("X-Request-Id", requestId);

  if (
    !headers.has("Content-Type") &&
    (fetchOptions.method === 'POST' || fetchOptions.method === 'PUT' || fetchOptions.method === 'PATCH')
  ) {
    headers.set("Content-Type", "application/json");
  }

  if (requireAuth) {
    const token = authToken();
    if (!token) {
      const error = new Error("No authentication token found") as ApiError;
      error.status = 401;

      if (typeof window !== 'undefined') {
        if (handleUnauthorized) {
          handleUnauthorized(error);
        } else {
          redirectToLogin();
        }
      }

      throw error;
    }
    headers.set("Authorization", `Bearer ${token}`);
  }

  try {
    const response = await fetch(API_CONFIG.getUrl(endpoint), {
      ...fetchOptions,
      headers,
    });

    if (response.status === 401) {
      localStorage.removeItem("auth_token");

      const error = new Error("Unauthorized: Please log in again") as ApiError;
      error.status = 401;
      error.statusText = response.statusText;
      error.requestId = requestId;
      reportApiFailure({ endpoint: diagnosticEndpoint, method, status: 401, componentName, requestId });

      if (handleUnauthorized) {
        handleUnauthorized(error);
      } else {
        if (typeof window !== 'undefined') {
          if (componentName !== 'AuthContext-checkAuth') {
            toaster.create({
              title: "Session Expired",
              description: "Your session has expired. Please log in again.",
              type: "error",
            });
          }
          redirectToLogin();
        }
      }

      throw error;
    }

    if (!response.ok) {
      let errorMessage = response.statusText;
      let errorCode: string | undefined;
      let reconciliationEntryIds: string[] | undefined;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
        // Machine-readable code (e.g. feature_not_entitled) lets callers branch.
        if (typeof errorData.code === 'string') errorCode = errorData.code;
        if (errorCode === 'paid_charge_reconciliation_required' &&
            Array.isArray(errorData.reconciliation_entry_ids) &&
            errorData.reconciliation_entry_ids.length === 2 &&
            errorData.reconciliation_entry_ids.every(
              (id: unknown) => typeof id === 'string' && /^[a-f\d]{24}$/i.test(id),
            )) {
          reconciliationEntryIds = errorData.reconciliation_entry_ids;
        }
      } catch {
        // Use status text if JSON parsing fails
      }

      const error = new Error(errorMessage) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      error.code = errorCode;
      error.reconciliationEntryIds = reconciliationEntryIds;
      error.requestId = requestId;
      reportApiFailure({
        endpoint: diagnosticEndpoint,
        method,
        status: response.status,
        code: errorCode,
        componentName,
        requestId,
        message: errorMessage,
      });
      throw error;
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();

      // Caching is disabled
      if (false && cacheKey) {
        log.debug(`Caching disabled for: ${cacheKey}`);
      }

      return data;
    }

    return null as T;
  } catch (error: unknown) {
    // Failures with a status were already reported above; anything else is a
    // network-level failure (DNS, CORS, offline) the backend never saw.
    if (!(error && typeof error === 'object' && 'status' in error)) {
      reportApiFailure({
        endpoint: diagnosticEndpoint,
        method,
        componentName,
        requestId,
        message: error instanceof Error ? error.message : String(error),
      });
    }
    throw error;
  }
}

/**
 * Fetch a non-JSON backend resource (CSV exports, detailed health) with the
 * same auth, correlation, and failure telemetry as `fetchApi`, returning the
 * raw `Response` for the caller to consume (`.text()`, `.json()`). Callers
 * keep their own error-message semantics; failures are reported here so the
 * export/health paths are not blind spots.
 */
export async function fetchRawApi(
  endpoint: string,
  options: FetchOptions = {},
  componentName: string = 'Unknown'
): Promise<Response> {
  const { requireAuth = true, handleUnauthorized, ...fetchOptions } = options;
  const method = fetchOptions.method || 'GET';
  const diagnosticEndpoint = redactApiEndpoint(endpoint);
  const requestId = newRequestId();

  const headers = new Headers(fetchOptions.headers || {});
  headers.set('X-Request-Id', requestId);
  if (requireAuth) {
    const token = typeof localStorage !== 'undefined' ? authToken() : null;
    if (!token) {
      const error = new Error('No authentication token found') as ApiError;
      error.status = 401;
      if (handleUnauthorized) handleUnauthorized(error);
      else redirectToLogin();
      throw error;
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(API_CONFIG.getUrl(endpoint), { ...fetchOptions, headers });
  } catch (error: unknown) {
    reportApiFailure({
      endpoint: diagnosticEndpoint,
      method,
      componentName,
      requestId,
      message: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }

  if (response.status === 401) {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('auth_token');
    const error = new Error('Unauthorized: Please log in again') as ApiError;
    error.status = 401;
    error.statusText = response.statusText;
    error.requestId = requestId;
    reportApiFailure({ endpoint: diagnosticEndpoint, method, status: 401, componentName, requestId });
    if (handleUnauthorized) {
      handleUnauthorized(error);
    } else {
      toaster.create({
        title: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
        type: 'error',
      });
      redirectToLogin();
    }
    throw error;
  }

  if (!response.ok) {
    reportApiFailure({ endpoint: diagnosticEndpoint, method, status: response.status, componentName, requestId });
  }
  return response;
}
